/**
 * GuardLiveCard — card que aparece no topo da tela do char (`CharacterDetailPage`)
 * mostrando dados live do heartbeat AntiCheat + acumulando historico em memoria.
 *
 * Polling: a cada 5s busca `getCharacterGuardOnline(charIdx)` e empilha snapshot.
 * Historico vive enquanto o componente esta montado — sair da pagina zera.
 *
 * Historico EVENT-DRIVEN: grava uma linha quando o HEADER muda (posicao OU mapId).
 * Player parado (macro no lugar) = 1 linha; o tempo parado sai de now - capturedAt.
 * Player andando = 1 linha por mudanca de posicao. Sem amostragem por delay.
 *
 * Detector heuristico de padrao (in-memory, sem chamadas extra) — mede por DURACAO
 * (nao por contagem de amostras, que dependia da cadencia fixa):
 *   - "FIXO" — mesmo mapId por >= 3min, raio < 1500 (dwell da run atual ate now)
 *   - "MOVIMENTO PEQUENO" — raio < 800 por >= 5min (provavel macro AOE)
 *   - "DG REPETIDA" — entra e sai da mesma dungeon >= 2 vezes em janela
 *   - "TRAJETORIA REPETIDA" — mesmo path numa DG (DTW normalizado por tempo)
 *   - "OFFLINE/AUSENTE" — sem novos frames ha > 60s
 *
 * Linhas do historico mudam de cor quando muda worldId (separador visual + label).
 */
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { ShieldCheck, MapPin, Activity, Coins, Clock, AlertTriangle, Eye, Download } from 'lucide-react';
import { getCharacterGuardOnline, type GuardOnlineRow } from '@/shared/services/adminService';
import {
  useGuardSocket,
  type GuardPosFrame,
  type GuardHistPoint,
  type GuardHistMsg,
  type GuardServerAlert,
} from '@/shared/hooks/useGuardSocket';
import { worldName } from '@/features/Notifications/worldNames';

export type GuardSnapshot = {
  capturedAt: number;          // Date.now() no momento do fetch
  mapId: number | null;
  posX: number | null;
  posY: number | null;
  alz: number | null;
  speed: number | null;
  bikeSpeed: number | null;
  lastHeartbeatAt: string | null;
};

const HISTORY_MAX = 4000;      // teto de linhas in-memory. Agora event-driven: player
                               // parado = 1 linha (dwell sai de now-capturedAt); player
                               // andando = 1 linha por mudanca de posicao (~ate 4/s).
const DETECT_TICK_MS = 5000;   // recalcula os detectores a cada 5s (alem de a cada
                               // mudanca de historico) — pega dwell de quem esta parado.
const STALE_HB_SEC = 60;       // sem frame ha mais que isso = ausente

export function GuardLiveCard({ charIdx }: { charIdx: number }) {
  const [snap, setSnap] = useState<GuardOnlineRow | null>(null);
  const [history, setHistory] = useState<GuardSnapshot[]>([]);
  // [BACKFILL] alertas do MoveGuard vindos do SERVIDOR (speed/macro). Chegam no
  // backfill (historico acumulado na API) e ao vivo enquanto a tela esta aberta.
  // Sao AUTORITATIVOS/complementares aos padroes detectados no client (`patterns`).
  const [serverAlerts, setServerAlerts] = useState<GuardServerAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // snap mais recente (live) num ref — os detectores usam pra medir o dwell de quem
  // esta parado (sem gerar novos pontos) sem re-renderizar a cada frame.
  const snapRef = useRef<GuardOnlineRow | null>(null);
  // clock 5s: recalcula "AUSENTE" + os detectores por duracao mesmo sem novos frames.
  const [clock, forceClock] = useState(0);

  // Reset ao trocar de char
  useEffect(() => {
    setHistory([]); setSnap(null); setError(null); setLoading(true);
    setServerAlerts([]);
    snapRef.current = null;
  }, [charIdx]);

  // Seed one-shot: pega charName + primeira leitura (WS pode demorar o handshake).
  // Nao clobbera dados live que ja tenham chegado por WS (merge de charName).
  useEffect(() => {
    let alive = true;
    getCharacterGuardOnline(charIdx)
      .then((res) => {
        if (!alive) return;
        setSnap((s) => (s ? { ...s, charName: s.charName ?? res.charName } : res));
        setLoading(false);
      })
      .catch(() => { /* WS assume; sem seed */ });
    return () => { alive = false; };
  }, [charIdx]);

  // Clock 5s: badge de staleness + recalcula os detectores por duracao (pega quem
  // esta parado ha' >= 3min sem gerar pontos novos).
  useEffect(() => {
    const id = window.setInterval(() => forceClock((t) => t + 1), DETECT_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Cada frame WS: atualiza o header (live) e grava no historico SO' quando o header
  // muda (posicao OU mapId). Sem gate por tempo — event-driven.
  const applyFrame = useCallback((m: GuardPosFrame) => {
    const nowMs = Date.now();
    const nowIso = new Date(nowMs).toISOString();
    const mapId = m.mapId ?? null;
    const posX = m.x ?? null;
    const posY = m.y ?? null;
    setError(null);
    setLoading(false);
    const live: GuardOnlineRow = {
      charIdx,
      charName: snapRef.current?.charName ?? null,
      userNum: m.userNum ?? snapRef.current?.userNum ?? null,
      mapId,
      posX,
      posY,
      alz: m.alz ?? null,
      speed: m.speed ?? null,
      bikeSpeed: m.bikeSpeed ?? null,
      lastHeartbeatAt: nowIso,        // chegada do frame = prova de vida
    } as GuardOnlineRow;
    snapRef.current = live;
    setSnap((prev) => ({ ...(prev || {}), ...live, charName: prev?.charName ?? live.charName }));

    // Grava so' se mudou posicao ou mapId vs a ultima linha (header mudou).
    // Player parado = nenhuma linha nova; o tempo parado sai de now - capturedAt.
    setHistory((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.mapId === mapId && last.posX === posX && last.posY === posY) {
        return prev;
      }
      const next: GuardSnapshot = {
        capturedAt: nowMs, mapId, posX, posY,
        alz: m.alz ?? null, speed: m.speed ?? null, bikeSpeed: m.bikeSpeed ?? null,
        lastHeartbeatAt: nowIso,
      };
      const merged = [...prev, next];
      if (merged.length > HISTORY_MAX) merged.splice(0, merged.length - HISTORY_MAX);
      return merged;
    });
  }, [charIdx]);

  // [BACKFILL] chega logo apos o watch: SEED do historico (a .so so' TRANSMITE;
  // o estado acumulado vive na API) + alertas ja detectados. A tela abre
  // preenchida — nao espera mais o player se mexer pra ter dados.
  const applyHist = useCallback((h: GuardHistMsg) => {
    setLoading(false);
    if (h.points?.length) {
      setHistory((prev) => mergeBackfill(prev, h.points));
    }
    if (h.alerts?.length) {
      setServerAlerts(dedupeAlerts(h.alerts));
    }
  }, []);

  // Alerta do MoveGuard ao vivo (a tela ja esta aberta): empilha e dedupa.
  const applyAlert = useCallback((a: GuardServerAlert) => {
    setServerAlerts((prev) => dedupeAlerts([...prev, a]));
  }, []);

  useGuardSocket(charIdx, applyFrame, { onHist: applyHist, onAlert: applyAlert });

  // Recalcula quando o historico muda (novo movimento) E a cada tick de 5s (dwell de
  // quem esta parado). Usa a posicao live (snapRef) pra estender a run atual ate now.
  const patterns = useMemo(
    () => detectPatterns(history, snapRef.current, Date.now()),
    [history, clock],
  );
  const hbAgeSec = snap?.lastHeartbeatAt
    ? Math.max(0, Math.floor((Date.now() - new Date(snap.lastHeartbeatAt).getTime()) / 1000))
    : null;
  const stale = hbAgeSec != null && hbAgeSec > STALE_HB_SEC;

  // [REMOVIDO 2026-07-29] A tela do personagem NAO detecta mais nada no navegador
  // nem cria alerta (era o source 'panel_guard_live'). Agora e' so' um VISUALIZADOR
  // do guard automatico (moveguard, server-side): a deteccao e os alertas vivem
  // 100% no servidor (_39_notifications). O navegador nunca gera alerta.

  return (
    <div className="surface-card rounded-xl p-5 mb-6 border border-white/[0.04]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-md flex items-center justify-center bg-amber-500/15 text-amber-400">
            <ShieldCheck size={16} />
          </span>
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Guard
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded
                ${stale
                    ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                    : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${stale ? 'bg-red-400' : 'bg-emerald-400 animate-pulse'}`} />
                {stale ? `AUSENTE ${hbAgeSec}s` : 'LIVE'}
              </span>
            </h2>
            <p className="text-[11px] text-white/55 mt-0.5">
              Heartbeat AntiCheat · tempo real (WS) · histórico em memória ({history.length}/{HISTORY_MAX})
            </p>
          </div>
        </div>
        {loading && history.length === 0 && (
          <span className="text-[10px] text-white/40 font-mono">carregando...</span>
        )}
      </div>

      {/* Header live: world, posição, alz, hb, speed */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <LiveField label="World" icon={<MapPin size={13} />}
          value={snap?.mapId != null ? `${worldName(snap.mapId)} (${snap.mapId})` : '—'}
          accent={!!snap?.mapId} />
        <LiveField label="Posição" icon={<Activity size={13} />}
          value={snap?.posX != null && snap?.posY != null
            ? `${snap.posX.toFixed(0)}, ${snap.posY.toFixed(0)}`
            : '—'} mono />
        <LiveField label="Alz" icon={<Coins size={13} />}
          value={snap?.alz != null ? snap.alz.toLocaleString('pt-BR') : '—'} mono />
        <LiveField label="Último HB" icon={<Clock size={13} />}
          value={hbAgeSec != null ? `${hbAgeSec}s atrás` : '—'} mono
          danger={stale} />
        <LiveField label="Speed/Bike" icon={<Activity size={13} />}
          value={snap?.speed != null
            ? `${snap.speed.toFixed(1)} / ${(snap.bikeSpeed ?? 0).toFixed(1)}`
            : '—'} mono />
      </div>

      {error && (
        <div className="mb-3 rounded-lg p-2.5 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
          <AlertTriangle size={13} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Alertas do servidor (MoveGuard: speed/macro) — autoritativos, vindos da
          API/backfill. Ficam separados dos padrões heurísticos do client. */}
      {serverAlerts.length > 0 && (
        <div className="mb-4">
          <h3 className="text-[10px] font-bold text-white/45 uppercase tracking-wider mb-2">
            Alertas AntiCheat ({serverAlerts.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {serverAlerts.slice().reverse().map((a, i) => (
              <span key={`${a.kind}-${a.ts ?? i}`} title={serverAlertDetail(a)}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md cursor-help
                  ${a.kind === 'speed'
                    ? 'bg-red-500/15 border border-red-500/40 text-red-300'
                    : 'bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300'}`}>
                <AlertTriangle size={11} />
                {serverAlertLabel(a)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Padrões detectados */}
      {patterns.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {patterns.map((p) => (
            <span key={p.id} title={p.detail}
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md cursor-help
                ${severityClass(p.severity)}`}>
              <Eye size={11} />
              {p.label}
            </span>
          ))}
        </div>
      )}

      {/* Histórico in-memory */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-bold text-white/65 uppercase tracking-wider">
            Histórico de posição
          </h3>
          {history.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => downloadHistory(charIdx, history, snap?.charName ?? null)}
                title="Baixar como TXT"
                className="inline-flex items-center gap-1 text-[10px] font-semibold
                           text-[#00e1d7] hover:text-white px-2 py-1 rounded-md
                           bg-[#00e1d7]/10 border border-[#00e1d7]/25
                           hover:bg-[#00e1d7]/20 transition cursor-pointer">
                <Download size={11} />
                Baixar
              </button>
              <button onClick={() => setHistory([])}
                className="text-[10px] text-white/45 hover:text-white/75 transition cursor-pointer">
                limpar
              </button>
            </div>
          )}
        </div>

        {history.length === 0 ? (
          <p className="text-xs text-white/40 italic py-6 text-center">
            Aguardando primeira leitura...
          </p>
        ) : (
          <div className="h-64 overflow-y-auto rounded-lg border border-white/[0.04] bg-[#0d141b]">
            <HistoryTable history={history} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  History table — destaca mudanças de world
// ─────────────────────────────────────────────────────────────────────────────
function HistoryTable({ history }: { history: GuardSnapshot[] }) {
  // Render do mais recente pro mais antigo
  const rows = [...history].reverse();
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-[#0d141b] z-10">
        <tr className="text-[10px] uppercase text-white/45 tracking-wider">
          <th className="text-left px-3 py-2 font-bold">Quando</th>
          <th className="text-left px-3 py-2 font-bold">World</th>
          <th className="text-left px-3 py-2 font-bold">X</th>
          <th className="text-left px-3 py-2 font-bold">Y</th>
          <th className="text-left px-3 py-2 font-bold">Alz</th>
          <th className="text-left px-3 py-2 font-bold">Δ</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s, i) => {
          // O "anterior cronologico" do reverse e' o proximo no array
          const prev = rows[i + 1];
          const worldChanged = prev != null && prev.mapId !== s.mapId;
          const dist = prev != null && prev.posX != null && s.posX != null && prev.posY != null && s.posY != null
            ? Math.round(Math.hypot(s.posX - prev.posX, s.posY - prev.posY))
            : null;
          return (
            <tr key={s.capturedAt}
                className={`border-t border-white/[0.03]
                  ${worldChanged ? 'bg-amber-500/[0.08] hover:bg-amber-500/[0.12]' : 'hover:bg-white/[0.02]'}`}>
              <td className="px-3 py-1.5 font-mono text-white/60 whitespace-nowrap">
                {new Date(s.capturedAt).toLocaleTimeString('pt-BR')}
              </td>
              <td className="px-3 py-1.5 font-mono">
                {worldChanged ? (
                  <span className="text-amber-400 font-bold">
                    {s.mapId ?? '?'} ⇐ {prev?.mapId ?? '?'}
                  </span>
                ) : (
                  <span className="text-white/75">{s.mapId ?? '—'}</span>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-white/65">{s.posX?.toFixed(0) ?? '—'}</td>
              <td className="px-3 py-1.5 font-mono text-white/65">{s.posY?.toFixed(0) ?? '—'}</td>
              <td className="px-3 py-1.5 font-mono text-white/55">
                {s.alz != null ? s.alz.toLocaleString('pt-BR') : '—'}
              </td>
              <td className="px-3 py-1.5 font-mono text-white/45">
                {dist != null ? `${dist}` : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Pattern detector — heuristicas in-memory
// ─────────────────────────────────────────────────────────────────────────────
type Pattern = { id: string; label: string; detail: string; severity: 'info' | 'warn' | 'danger' };

function detectPatterns(history: GuardSnapshot[], live: GuardOnlineRow | null, nowMs: number): Pattern[] {
  if (history.length < 1) return [];
  const out: Pattern[] = [];
  const last = history[history.length - 1];
  const curMap = last.mapId;

  // "Run atual" = sequencia contigua no MESMO mapId terminando no ultimo ponto.
  // Estende ate' AGORA (now) usando a posicao live — player parado nao gera pontos
  // novos, entao o tempo no mapa e' now - inicio da run (nao a contagem de amostras).
  const runPts: GuardSnapshot[] = [];
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].mapId !== curMap) break;
    runPts.unshift(history[i]);
  }
  const liveSameMap = !!live && (live.mapId ?? null) === curMap;
  const nowX = liveSameMap ? (live!.posX ?? last.posX) : last.posX;
  const nowY = liveSameMap ? (live!.posY ?? last.posY) : last.posY;
  const dwellMs = nowMs - runPts[0].capturedAt;               // tempo no mapa atual
  const xs = runPts.map((s) => s.posX ?? 0).concat(nowX ?? 0);
  const ys = runPts.map((s) => s.posY ?? 0).concat(nowY ?? 0);
  const radius = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));

  // 1) FIXO — >= 3min no mesmo mapId, raio < 1500 (parado OU raio apertado).
  if (curMap != null && dwellMs >= 3 * 60 * 1000 && radius < 1500) {
    out.push({
      id: 'fixed',
      label: `FIXO em ${worldName(curMap)}`,
      detail: `${Math.floor(dwellMs / 1000)}s no mesmo mapa, raio ${Math.round(radius)}u (${runPts.length} ${runPts.length === 1 ? 'ponto' : 'pontos'})`,
      severity: radius < 800 ? 'danger' : 'warn',
    });
  }

  // 2) MOVIMENTO PEQUENO — >= 5min com raio < 800 (macro AOE/spot). So' se nao ja' FIXO.
  if (curMap != null && dwellMs >= 5 * 60 * 1000 && radius < 800 && !out.some((p) => p.id === 'fixed')) {
    out.push({
      id: 'small-radius',
      label: 'MOVIMENTO PEQUENO (5min)',
      detail: `${Math.floor(dwellMs / 1000)}s, raio ${Math.round(radius)}u — provavel macro AOE/spot`,
      severity: 'warn',
    });
  }

  // 3) DG REPETIDA — entra e sai do mesmo mapId >= 2x (transicoes worldA→worldB→worldA)
  const transitions: number[] = [];
  for (let i = 1; i < history.length; i++) {
    if (history[i].mapId !== history[i - 1].mapId && history[i].mapId != null) {
      transitions.push(history[i].mapId as number);
    }
  }
  if (transitions.length >= 4) {
    const counts: Record<number, number> = {};
    transitions.forEach((m) => { counts[m] = (counts[m] || 0) + 1; });
    const repeated = Object.entries(counts).filter(([, c]) => c >= 2);
    if (repeated.length >= 1) {
      const [mapStr, count] = repeated.sort((a, b) => b[1] - a[1])[0];
      out.push({
        id: 'dg-repeated',
        label: `${worldName(Number(mapStr))} REPETIDO ${count}x`,
        detail: `${worldName(Number(mapStr))} (World ${mapStr}) apareceu ${count}x em ${transitions.length} transições — possível farm automatizado`,
        severity: count >= 3 ? 'danger' : 'warn',
      });
    }
  }

  // 4) Alz crescente sem trocar de mapa na run atual = farm (info). Usa o alz live.
  if (curMap != null && dwellMs >= 3 * 60 * 1000) {
    const alz0 = runPts.map((s) => s.alz).find((a) => a != null && a > 0);
    const alzNow = (liveSameMap ? live!.alz : null) ?? [...runPts].reverse().map((s) => s.alz).find((a) => a != null && a > 0);
    if (alz0 != null && alzNow != null) {
      const gain = alzNow - alz0;
      if (gain > 100_000) {
        out.push({
          id: 'alz-gain',
          label: `+${gain.toLocaleString('pt-BR')} Alz`,
          detail: `Ganho de alz alto sem trocar de mapa (${Math.floor(dwellMs / 1000)}s)`,
          severity: 'info',
        });
      }
    }
  }

  // 5) TRAJETÓRIA REPETIDA — compara current run com runs antigas no MESMO world.
  // Agrupa o historico em "runs" (sequencias contiguas no mesmo mapId), e pra
  // cada par (current_run, past_run_no_mesmo_world) calcula similaridade de
  // trajetoria normalizada por tempo (DTW simplificado).
  const macroPatterns = detectMacroRepetition(history);
  out.push(...macroPatterns);

  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Macro detector — agrupa runs por world e compara trajetorias
// ─────────────────────────────────────────────────────────────────────────────
type Run = {
  worldId: number;
  startedAt: number;
  endedAt: number;
  points: GuardSnapshot[];
};

function extractRuns(history: GuardSnapshot[]): Run[] {
  const runs: Run[] = [];
  let cur: Run | null = null;
  for (const s of history) {
    if (s.mapId == null || s.posX == null || s.posY == null) continue;
    if (cur && cur.worldId === s.mapId) {
      cur.points.push(s);
      cur.endedAt = s.capturedAt;
    } else {
      if (cur && cur.points.length >= 3) runs.push(cur);
      cur = { worldId: s.mapId, startedAt: s.capturedAt, endedAt: s.capturedAt, points: [s] };
    }
  }
  if (cur && cur.points.length >= 3) runs.push(cur);
  return runs;
}

/**
 * Similaridade de trajetoria entre duas runs (0-1, 1=identico).
 *
 * Normaliza tempo de cada ponto pelo offset desde o inicio da run, depois pra
 * cada ponto da run A, encontra ponto da run B no mesmo offset (interpolacao
 * linear) e calcula distancia euclidiana. Media baixa = trajetorias parecidas.
 *
 * Limite: 800u de distancia media equivale a similaridade 0. Linear ate la.
 * Funciona bem pra detectar player que segue o mesmo path numa DG porque
 * tempo entre snapshots e' fixo (5s), e quem usa macro normalmente bate offset
 * relativo dentro de margem.
 */
function compareTrajectories(a: Run, b: Run): number {
  if (a.worldId !== b.worldId) return 0;
  if (a.points.length < 3 || b.points.length < 3) return 0;

  // Limita comparacao ao tempo da run mais curta (overlap util)
  const aDuration = a.endedAt - a.startedAt;
  const bDuration = b.endedAt - b.startedAt;
  const compareWindow = Math.min(aDuration, bDuration);
  if (compareWindow < 30_000) return 0;  // minimo 30s pra ter algo significativo

  const aPoints = a.points.filter((p) => p.capturedAt - a.startedAt <= compareWindow);
  if (aPoints.length < 3) return 0;

  let totalDist = 0;
  let pairs = 0;

  for (const ap of aPoints) {
    const tOffset = ap.capturedAt - a.startedAt;       // ms desde inicio
    const bTarget = b.startedAt + tOffset;             // ms absoluto na run B
    const bp = interpolatePoint(b.points, bTarget);
    if (!bp || bp.posX == null || bp.posY == null || ap.posX == null || ap.posY == null) continue;
    totalDist += Math.hypot(ap.posX - bp.posX, ap.posY - bp.posY);
    pairs++;
  }
  if (pairs < 3) return 0;

  const avgDist = totalDist / pairs;
  // 0u = 1.0 similaridade, 800u = 0.0 — linear
  return Math.max(0, 1 - avgDist / 800);
}

function interpolatePoint(points: GuardSnapshot[], targetMs: number): GuardSnapshot | null {
  if (!points.length) return null;
  if (targetMs <= points[0].capturedAt) return points[0];
  if (targetMs >= points[points.length - 1].capturedAt) return points[points.length - 1];
  for (let i = 1; i < points.length; i++) {
    if (points[i].capturedAt >= targetMs) {
      const p0 = points[i - 1];
      const p1 = points[i];
      const span = p1.capturedAt - p0.capturedAt;
      if (span === 0 || p0.posX == null || p1.posX == null || p0.posY == null || p1.posY == null) return p0;
      const t = (targetMs - p0.capturedAt) / span;
      return {
        ...p0,
        capturedAt: targetMs,
        posX: p0.posX + (p1.posX - p0.posX) * t,
        posY: p0.posY + (p1.posY - p0.posY) * t,
      };
    }
  }
  return points[points.length - 1];
}

function detectMacroRepetition(history: GuardSnapshot[]): Pattern[] {
  const runs = extractRuns(history);
  if (runs.length < 2) return [];

  const out: Pattern[] = [];
  // Agrupa runs por worldId
  const byWorld = new Map<number, Run[]>();
  for (const r of runs) {
    if (!byWorld.has(r.worldId)) byWorld.set(r.worldId, []);
    byWorld.get(r.worldId)!.push(r);
  }

  for (const [worldId, worldRuns] of byWorld) {
    if (worldRuns.length < 2) continue;
    // Compara cada run com TODAS anteriores
    let bestPairSim = 0;
    let bestPair: [number, number] | null = null;
    const allSims: number[] = [];
    for (let i = 1; i < worldRuns.length; i++) {
      for (let j = 0; j < i; j++) {
        const sim = compareTrajectories(worldRuns[i], worldRuns[j]);
        allSims.push(sim);
        if (sim > bestPairSim) {
          bestPairSim = sim;
          bestPair = [j, i];
        }
      }
    }

    if (bestPairSim >= 0.6 && bestPair) {
      const matchPct = Math.round(bestPairSim * 100);
      const [j, i] = bestPair;
      const runI = worldRuns[i];
      const runJ = worldRuns[j];
      const durI = Math.floor((runI.endedAt - runI.startedAt) / 1000);
      const durJ = Math.floor((runJ.endedAt - runJ.startedAt) / 1000);
      out.push({
        id: `macro-${worldId}`,
        label: `TRAJETÓRIA REPETIDA em ${worldName(worldId)} (${matchPct}%)`,
        detail: `${worldName(worldId)} — Run ${i + 1} (~${durI}s) vs Run ${j + 1} (~${durJ}s) seguem o mesmo caminho — provável macro/script de DG`,
        severity: bestPairSim >= 0.8 ? 'danger' : 'warn',
      });
    }
  }

  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Backfill — funde o historico continuo da API + normaliza alertas do servidor
// ─────────────────────────────────────────────────────────────────────────────

// Funde o historico continuo vindo da API (POSBATCH ~1/s, RAW — pode ter muitos
// pontos identicos de quem ficou parado) com o que ja' foi coletado ao vivo.
// Ordena por tempo e COLAPSA pontos consecutivos identicos (mesma semantica
// event-driven do applyFrame: parado = 1 linha; o dwell sai de now - capturedAt
// do primeiro ponto da run). Idempotente em reconexao (o backfill reenvia os
// mesmos ts -> colapsam de volta pra 1 linha).
//
// NOTA de clock: ts do backfill e' wall-clock da API; capturedAt live e'
// Date.now() do browser. Ambos epoch ms — o skew de relogio e' irrelevante pra
// ordenacao/exibicao.
function mergeBackfill(prev: GuardSnapshot[], points: GuardHistPoint[]): GuardSnapshot[] {
  const mapped: GuardSnapshot[] = points.map((p) => ({
    capturedAt: p.ts,
    mapId: p.mapId ?? null,
    posX: p.x ?? null,
    posY: p.y ?? null,
    alz: null,                       // POSBATCH nao carrega alz
    speed: p.speed ?? null,
    bikeSpeed: null,
    lastHeartbeatAt: new Date(p.ts).toISOString(),
  }));
  const all = [...mapped, ...prev].sort((a, b) => a.capturedAt - b.capturedAt);
  const collapsed: GuardSnapshot[] = [];
  for (const s of all) {
    const last = collapsed[collapsed.length - 1];
    if (last && last.mapId === s.mapId && last.posX === s.posX && last.posY === s.posY) continue;
    collapsed.push(s);
  }
  if (collapsed.length > HISTORY_MAX) collapsed.splice(0, collapsed.length - HISTORY_MAX);
  return collapsed;
}

// Ordena por ts asc e remove duplicados exatos (kind+ts+mapId) — o backfill pode
// reenviar em reconexao. Mantem no maximo os ultimos 50 (teto do servidor tb).
function dedupeAlerts(alerts: GuardServerAlert[]): GuardServerAlert[] {
  const seen = new Set<string>();
  const out: GuardServerAlert[] = [];
  for (const a of [...alerts].sort((x, y) => (x.ts ?? 0) - (y.ts ?? 0))) {
    const key = `${a.kind}:${a.ts ?? 0}:${a.mapId ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out.slice(-50);
}

function serverAlertLabel(a: GuardServerAlert): string {
  if (a.kind === 'speed') {
    const ups = a.observedUps != null ? a.observedUps.toFixed(0) : '?';
    const ratio = a.ratio != null ? `x${a.ratio.toFixed(1)}` : '';
    return `SPEED ${ups}ups ${ratio}`.trim();
  }
  if (a.kind === 'macro') {
    return `MACRO ${worldName(a.mapId ?? 0)}`;
  }
  return String(a.kind).toUpperCase();
}

function serverAlertDetail(a: GuardServerAlert): string {
  const when = a.ts ? new Date(a.ts).toLocaleTimeString('pt-BR') : '';
  if (a.kind === 'speed') {
    return `${when} — velocidade observada ${a.observedUps?.toFixed(1) ?? '?'}ups `
      + `(sustentada ${a.sustainedUps?.toFixed(1) ?? '?'}, ratio ${a.ratio?.toFixed(2) ?? '?'}, `
      + `stat ${a.speedStat ?? '?'}) no mapa ${a.mapId ?? '?'}`;
  }
  if (a.kind === 'macro') {
    return `${when} — área ${a.areaSpan ?? '?'}u, movendo ${Math.round((a.movingFrac ?? 0) * 100)}% do tempo, `
      + `revisitas ${a.revisit ?? '?'}, ${a.uniqueCells ?? '?'} células únicas em ${a.windowSec ?? '?'}s `
      + `(${a.samples ?? '?'} amostras)`;
  }
  return when;
}

function severityClass(s: 'info' | 'warn' | 'danger'): string {
  switch (s) {
    case 'danger': return 'bg-red-500/15 border border-red-500/40 text-red-300';
    case 'warn':   return 'bg-amber-500/15 border border-amber-500/40 text-amber-300';
    case 'info':   return 'bg-sky-500/15 border border-sky-500/40 text-sky-300';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Download helper — serializa o historico in-memory pra TXT e dispara download
// ─────────────────────────────────────────────────────────────────────────────
function downloadHistory(charIdx: number, history: GuardSnapshot[], charName: string | null) {
  if (!history.length) return;

  const startedAt = new Date(history[0].capturedAt);
  const endedAt = new Date(history[history.length - 1].capturedAt);
  const durationSec = Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000);

  const header = [
    `# Guard history — CharIdx ${charIdx}${charName ? ` (${charName})` : ''}`,
    `# Capturado por painel GM — heartbeat AntiCheat tempo real (WS)`,
    `# Inicio  : ${startedAt.toISOString()} (${startedAt.toLocaleString('pt-BR')})`,
    `# Fim     : ${endedAt.toISOString()} (${endedAt.toLocaleString('pt-BR')})`,
    `# Duracao : ${durationSec}s (~${Math.floor(durationSec / 60)}min)`,
    `# Total   : ${history.length} snapshots`,
    '#',
    '# Colunas: timestamp_iso | epoch_ms | worldId | posX | posY | alz | speed | bikeSpeed | lastHeartbeatAt | delta(distance_to_previous)',
    '#',
  ].join('\n');

  const lines: string[] = [];
  let prev: GuardSnapshot | null = null;
  for (const s of history) {
    const delta = prev != null && prev.posX != null && s.posX != null && prev.posY != null && s.posY != null
      ? Math.round(Math.hypot(s.posX - prev.posX, s.posY - prev.posY))
      : 0;
    const worldChanged = prev != null && prev.mapId !== s.mapId ? ' [WORLD CHANGE]' : '';
    lines.push([
      new Date(s.capturedAt).toISOString(),
      String(s.capturedAt),
      s.mapId ?? '',
      s.posX != null ? s.posX.toFixed(0) : '',
      s.posY != null ? s.posY.toFixed(0) : '',
      s.alz ?? '',
      s.speed != null ? s.speed.toFixed(2) : '',
      s.bikeSpeed != null ? s.bikeSpeed.toFixed(2) : '',
      s.lastHeartbeatAt ?? '',
      String(delta),
    ].join(' | ') + worldChanged);
    prev = s;
  }

  const content = header + '\n' + lines.join('\n') + '\n';
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const tsName = endedAt.toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
  const filename = `guard_char${charIdx}_${tsName}.txt`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─────────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function LiveField({
  label, icon, value, mono, accent, danger,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  mono?: boolean;
  accent?: boolean;
  danger?: boolean;
}) {
  const valueClass = [
    'text-base font-bold leading-none',
    mono ? 'font-mono' : '',
    danger ? 'text-red-400' : accent ? 'text-[#00e1d7]' : 'text-white',
  ].join(' ');
  return (
    <div className="bg-[#0d141b] rounded-lg p-2.5 border border-white/[0.04]">
      <div className="flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider font-bold mb-1.5">
        <span className="text-white/65">{icon}</span>
        {label}
      </div>
      <p className={valueClass}>{value}</p>
    </div>
  );
}
